import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Eye, EyeOff } from 'lucide-react';
import type { CredentialRotateInput, ProviderSlug } from '@runik/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Spinner } from '../ui/spinner';
import { toast } from '../ui/sonner';
import { ApiError } from '../../lib/auth/client';
import { rotateCredential } from '../../lib/queries/credentials';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  credentialId: string;
  label: string;
  providerSlug: ProviderSlug;
}

export function RotateDialog({ open, onOpenChange, credentialId, label, providerSlug }: Props) {
  const qc = useQueryClient();
  const [value, setValue] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setValue('');
      setShowKey(false);
      setError(null);
    }
  }, [open]);

  const rotate = useMutation({
    mutationFn: (body: CredentialRotateInput) => rotateCredential(credentialId, body),
    onSuccess: () => {
      toast.success('Yenilendi');
      qc.invalidateQueries({ queryKey: ['credentials'] });
      onOpenChange(false);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.problem.detail ?? err.problem.title : 'Yenileme başarısız');
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (providerSlug === 'openai') {
      const trimmed = value.trim();
      if (!/^proj_[A-Za-z0-9_-]+$/.test(trimmed)) {
        setError('Proje ID\'si `proj_…` formatında olmalı');
        return;
      }
      rotate.mutate({ providerSlug: 'openai', newProjectId: trimmed });
    } else {
      if (value.length < 8) {
        setError('API anahtarı en az 8 karakter olmalı');
        return;
      }
      rotate.mutate({ providerSlug: 'replicate', newApiKey: value });
    }
  };

  const isOpenAI = providerSlug === 'openai';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isOpenAI ? 'Proje ID Değiştir' : 'Anahtarı Yenile'}</DialogTitle>
          <DialogDescription>
            <strong>{label}</strong> için {isOpenAI ? 'yeni proje ID girin' : 'yeni bir anahtar girin. Eski anahtar kalıcı olarak silinir'}.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="space-y-2">
            <Label htmlFor="rotateValue">{isOpenAI ? 'Yeni Proje ID' : 'Yeni API Anahtarı'}</Label>
            <div className="relative">
              <Input
                id="rotateValue"
                type={isOpenAI || showKey ? 'text' : 'password'}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={isOpenAI ? 'proj_abc123…' : ''}
                autoComplete="off"
                autoFocus
              />
              {!isOpenAI && (
                <button
                  type="button"
                  onClick={() => setShowKey((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
                >
                  {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              İptal
            </Button>
            <Button type="submit" variant={isOpenAI ? 'default' : 'destructive'} disabled={rotate.isPending}>
              {rotate.isPending ? (
                <>
                  <Spinner /> Kaydediliyor…
                </>
              ) : isOpenAI ? (
                'Kaydet'
              ) : (
                'Yenile'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
